// Copyright ©️ 2026 eVolve MEP, LLC

import { type TaskTypeGroup, type TaskType, useEvolvePost, useEvolvePut } from '@evolvemep/api-ts';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { AttachmentSection } from 'components/Mantine/Attachments/AttachmentSection';
import { uploadFileIfNeeded, uploadFilesIfNeeded } from 'components/Mantine/FileUploads/uploadToPresignedUrl';
import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { useFormModal, FormModal } from 'components/Mantine/Modals/FormModal';
import { createdKey } from 'components/Mantine/TypeSafeSelect/Creatables/useCreatable';
import type { TaskTypeUpsertResponse } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';
import type { TaskTypeFormValues } from 'modules/Shop/Tasks/TaskTypes/types';

import { TaskTypeFormComponents } from './TaskTypeFormComponents';

type CommonProps = {
  /** The task type to edit. Omit to create a new task type. */
  taskType?: TaskType;
  /** Group pre-filled when creating a new task type. */
  defaultTaskTypeGroup?: TaskTypeGroup;
  onTaskTypeCreated?: (taskType: TaskTypeUpsertResponse) => void;
  onTaskTypeUpdated?: (taskType: TaskTypeUpsertResponse) => void;
};

type TaskFormProps = CommonProps & {
  onClose: () => void;
};

type Props = ModalRegistryManagedProps & CommonProps;

const CreateEditTaskTypeForm = ({
  taskType,
  defaultTaskTypeGroup,
  onClose,
  onTaskTypeCreated,
  onTaskTypeUpdated,
}: TaskFormProps) => {
  const { apiCall: createTaskType } = useEvolvePost('shop/taskType');
  const { apiCall: updateTaskType } = useEvolvePut(`shop/taskType/:taskTypeId`);
  const { apiCall: createTaskGroup } = useEvolvePost('shop/taskTypeGroup');

  const form = useForm<TaskTypeFormValues>({
    initialValues: {
      name: taskType?.name ?? '',
      taskTypeGroup: isNotNil(taskType?.taskTypeGroupId)
        ? {
            taskTypeGroupId: taskType.taskTypeGroupId,
            name: taskType.taskTypeGroupName,
          }
        : isNotNil(defaultTaskTypeGroup)
          ? {
              taskTypeGroupId: defaultTaskTypeGroup.taskTypeGroupId,
              name: defaultTaskTypeGroup.name,
            }
          : null,
      description: taskType?.description ?? '',
      defaultAssignees: taskType?.defaultAssignees ?? [],
      estimatedUnitTime: taskType?.estimatedUnitTime ?? null,
      attachments: [],
    },
    validate: {
      name: (t) => !t,
      estimatedUnitTime: (v) => Number.isNaN(v),
    },
  });

  const onSubmit = async ({
    taskTypeGroup,
    defaultAssignees: defaultAssigneeUsers,
    image,
    attachments,
    ...values
  }: typeof form.values) => {
    let taskTypeGroupId = taskTypeGroup?.taskTypeGroupId ?? null;
    if (taskTypeGroupId === createdKey) {
      const newTaskGroup = await createTaskGroup({ name: taskTypeGroup?.name ?? '' });
      taskTypeGroupId = newTaskGroup.taskTypeGroupId;
    }
    const defaultAssignees = defaultAssigneeUsers.map(({ userId }) => userId);

    const body = {
      ...values,
      defaultAssignees,
      taskTypeGroupId,
      image: image
        ? {
            fileName: image.name,
          }
        : undefined,
      files: attachments?.length
        ? attachments.filter((f) => f.type === 'file').map(({ file: { name } }) => ({ fileName: name }))
        : undefined,
      webLinks: attachments?.length ? attachments.filter((f) => f.type === 'webLink').map((a) => a.webLink) : undefined,
    };

    const isEditing = isNotNil(taskType);

    const newTaskType = await (isEditing
      ? updateTaskType(body, {
          url: `shop/taskType/${taskType.taskTypeId}`,
        })
      : createTaskType(body));

    await uploadFileIfNeeded(image, newTaskType.image);
    await uploadFilesIfNeeded(
      attachments?.filter((a) => a.type === 'file').map((a) => a.file),
      newTaskType.files?.map((f) => f.file),
    );

    notifications.show({
      title: `Successfully ${isEditing ? 'updated' : 'created'}`,
      message: `Task type ${newTaskType.name} ${isEditing ? 'updated' : 'created'}`,
      color: 'green',
    });

    if (isEditing) {
      onTaskTypeUpdated?.(newTaskType);
    } else {
      onTaskTypeCreated?.(newTaskType);
    }
    onClose();
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <TaskTypeFormComponents taskType={taskType} form={form} />
      {isNotNil(taskType) && (
        <AttachmentSection
          title="Default Attachments"
          entityType="taskType"
          entityId={taskType.taskTypeId}
          active
          description="Changes to attachments are saved automatically. These will be added to all new instances of this task."
        />
      )}
    </>
  );
};

/** Modal for creating a new task type, or editing `taskType` when provided. */
export const CreateEditTaskTypeModal = ({
  taskType,
  defaultTaskTypeGroup,
  onTaskTypeCreated,
  onTaskTypeUpdated,
  ...props
}: Props) => (
  <FormModal title={`${taskType ? 'Edit' : 'New'} task`} size="lg" type="submit" {...props}>
    <CreateEditTaskTypeForm
      taskType={taskType}
      defaultTaskTypeGroup={defaultTaskTypeGroup}
      onTaskTypeCreated={onTaskTypeCreated}
      onTaskTypeUpdated={onTaskTypeUpdated}
      onClose={props.onClose}
    />
  </FormModal>
);
